import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FALLBACK_FAQS = [
  { question: "How does Olivia Does work?", answer: "Tell us what you need in plain English. We categorise the job, assign the right in-house team member or vetted partner trade, and manage everything through to completion — you only ever deal with one trusted point of contact." },
  { question: "Are your tradespeople vetted?", answer: "Every tradesperson — staff or partner — goes through our 4-stage vetting: verified ID & insurance, confirmed trade credentials, phoned references, and a personally-reviewed trial period before they work in your home." },
  { question: "Do you offer same-day callouts?", answer: "Yes. For urgent jobs like leaks, electrical faults or lockouts we offer same-day and emergency callouts wherever a team is available in your area." },
  { question: "How is pricing handled?", answer: "Many common jobs have transparent fixed prices you can book instantly. For bespoke or larger work you'll receive a clear written quote before any work begins — one transparent quote, one bill." },
  { question: "What if I'm not happy with the work?", answer: "Every visit is quality reviewed and backed by our aftercare promise — 30 days on workmanship and 12 months on installations. If something isn't right, Olivia comes back and makes it right at no extra cost." },
];

export default function Faqs() {
  const [faqs, setFaqs] = useState(FALLBACK_FAQS);

  useEffect(() => {
    document.title = "FAQs — Olivia Does";
    api.get("/cms/faqs")
      .then((r) => { if (Array.isArray(r.data) && r.data.length) setFaqs(r.data); })
      .catch(() => {});
  }, []);

  // FAQPage JSON-LD for rich snippets
  useEffect(() => {
    const ld = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map((f) => ({
        "@type": "Question",
        "name": f.question,
        "acceptedAnswer": { "@type": "Answer", "text": f.answer },
      })),
    };
    const idTag = "od-jsonld-faq";
    let el = document.getElementById(idTag);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = idTag;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(ld);
    return () => { el && el.remove(); };
  }, [faqs]);

  return (
    <div data-testid="faqs-page" className="bg-[#FCFCFA]">
      <section className="max-w-3xl mx-auto px-6 md:px-8 pt-16 pb-12 md:pt-24 md:pb-16">
        <span className="od-label">Help centre</span>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-[1.05] mt-4">
          Questions, answered.
        </h1>
        <p className="text-lg text-[#78716C] mt-6 leading-relaxed">
          Everything you need to know about booking, vetting, pricing and our aftercare promise. Still unsure? We're one message away.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-8 pb-20">
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((f, i) => (
            <AccordionItem
              key={f.id || i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="od-card px-6 border-none"
            >
              <AccordionTrigger className="font-display text-lg md:text-xl text-left hover:no-underline py-5">
                {f.question}
              </AccordionTrigger>
              <AccordionContent className="text-[#78716C] leading-relaxed pb-5">
                {f.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-8 pb-24">
        <div className="od-marketing-card p-10 md:p-14 text-center">
          <h2 className="font-display text-3xl md:text-4xl">Still have a question?</h2>
          <p className="text-[#78716C] mt-3 max-w-xl mx-auto leading-relaxed">
            Book a job and one of our concierge team will be in touch, or browse the full service catalogue.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <Link to="/book" data-testid="faqs-cta-book"><button className="od-btn-primary">Book a job</button></Link>
            <Link to="/services" data-testid="faqs-cta-services"><button className="od-btn-outline">Browse services</button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
